/**
 * Cicon Component
 * Granular icon component wrapping Tabler icons with theme-based styling
 */
'use client';
import type { CSSProperties } from 'react';
import type { Icon as TablerIcon } from '@tabler/icons-react';
import { useMemo } from 'react';
import { ColorType, getColorValue } from '@/theme/colors';
import { IconPreset } from '@/constants/componentPresets';
export interface CiconProps {
    icon: TablerIcon;
    size?: number;
    color?: ColorType;
    opacity?: number;
    stroke?: number;
    preset?: IconPreset;
    className?: string;
    style?: CSSProperties;
}
interface PresetConfig {
    size: number;
    color: ColorType;
    opacity?: number;
    stroke: number;
}
const presetConfigs: Record<IconPreset, PresetConfig> = {
    [IconPreset.Default]: {
        size: 20,
        color: ColorType.White,
        stroke: 2,
    },
    [IconPreset.Active]: {
        size: 20,
        color: ColorType.Accent,
        opacity: 1,
        stroke: 2,
    },
    [IconPreset.Inactive]: {
        size: 20,
        color: ColorType.White,
        opacity: 0.5,
        stroke: 2,
    },
    [IconPreset.Small]: {
        size: 16,
        color: ColorType.White,
        stroke: 2,
    },
    [IconPreset.Large]: {
        size: 24,
        color: ColorType.White,
        stroke: 2,
    },
};
export function Cicon({ icon: Icon, size, color, opacity, stroke, preset = IconPreset.Default, className, style, }: CiconProps) {
    const presetConfig = presetConfigs[preset];
    // Merge preset with explicit props (explicit props override preset)
    const finalSize = size ?? presetConfig.size;
    const finalColor = color ?? presetConfig.color;
    const finalOpacity = opacity ?? presetConfig.opacity;
    const finalStroke = stroke ?? presetConfig.stroke;
    const iconStyle: CSSProperties = useMemo(() => ({
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: getColorValue(finalColor, finalOpacity),
        ...style,
    }), [finalColor, finalOpacity, style]);
    return (
    <span className={className} style={iconStyle}>
      <Icon size={finalSize} stroke={finalStroke}/>
    </span>
    );
}
